import React, { useCallback, useRef } from "react";
import { KatBadge, KatFileUpload } from "@amzn/katal-react";
import { useTranslation } from "react-i18next";
import type { LocalizedText, NestUseCase } from "src/api/interfaces/nest";
import { MAX_UPLOAD_FILES } from "../../../constants";
import { useAlertContext } from "src/components/Alert/AlertContext";
import { alertHandler } from "src/components/Alert/handlers";
import "./index.css";

interface NestUploadCardProps {
  useCase: NestUseCase;
  documentTypeName: LocalizedText;
  onFilesSelected: (files: File[]) => void;
}

export function NestUploadCard({
  useCase,
  documentTypeName,
  onFilesSelected,
}: NestUploadCardProps) {
  const { t, i18n } = useTranslation();
  const fileUploadRef = useRef<KatFileUpload.Element>(null);
  const { alerts, setAlerts } = useAlertContext();

  const handleFilesAttached = useCallback(
    (event: KatFileUpload.FilesAttachedEvent) => {
      const allFiles = event.detail.files;
      const files = allFiles.slice(0, MAX_UPLOAD_FILES);

      if (allFiles.length > MAX_UPLOAD_FILES) {
        alertHandler.createNewAlertEntry({
          fade: true,
          text: t("aic_nest_upload_max_files_warning", {
            count: MAX_UPLOAD_FILES,
          }),
          isSuccess: false,
          variant: "warning",
          alerts,
          setAlerts,
        });
      }

      if (files.length) {
        onFilesSelected(files);
      }
      fileUploadRef.current?.clearAllFiles();
    },
    [onFilesSelected, alerts, setAlerts, t]
  );

  return (
    <div className="nest-upload-card">
      <h2 className="nest-upload-card__title">
        {t("aic_nest_upload_title", {
          name: t(useCase.name.key, useCase.name.parameters),
        })}
      </h2>

      <KatFileUpload
        ref={fileUploadRef}
        multiple
        accept=".xml,.edi,.pdf"
        variant="large"
        onFilesAttached={handleFilesAttached}
        locale={i18n.language}
        required
      >
        <span slot="label"></span>
        <div slot="hint" className="nest-upload-card__file-upload-hint">
          <KatBadge
            className="nest-upload-card__doc-badge"
            label={t(documentTypeName.key, documentTypeName.parameters)}
            type="tag"
          />
          {t("aic_nest_upload_subtitle")}
        </div>
        <span slot="constraints-message">
          {t("aic_nest_upload_constraints")}
        </span>
      </KatFileUpload>
    </div>
  );
}
